/**
 * Bot Dashboard Page
 * Main monitoring dashboard for individual bot
 */
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import type { DashboardTab } from '../types/botMonitoring'
import { useBotStatus, useBotOffers, useBotLoans, useBotEarningsChart } from '../hooks/useBotMonitoring'
import StatusPanel from '../components/bot-dashboard/StatusPanel'
import OffersTable from '../components/bot-dashboard/OffersTable'
import LoansTable from '../components/bot-dashboard/LoansTable'
import EarningsChart from '../components/bot-dashboard/EarningsChart'
import ErrorBanner from '../components/bot-dashboard/ErrorBanner'

export default function BotDashboard() {
  const { id } = useParams<{ id: string }>()
  const botId = parseInt(id || '0', 10)
  const [activeTab, setActiveTab] = useState<DashboardTab>('status')
  const [dismissedError, setDismissedError] = useState(false)
  const [earningsDays, setEarningsDays] = useState(7)

  // Fetch data with 5-second polling
  const {
    data: statusData,
    isLoading: statusLoading,
    error: statusError,
  } = useBotStatus(botId, activeTab === 'status')

  const {
    data: offersData,
    isLoading: offersLoading,
    error: offersError,
  } = useBotOffers(botId, activeTab === 'status')

  const {
    data: loansData,
    isLoading: loansLoading,
    error: loansError,
  } = useBotLoans(botId, activeTab === 'loans')

  const {
    data: earningsData,
    isLoading: earningsLoading,
    error: earningsError,
  } = useBotEarningsChart(botId, earningsDays, activeTab === 'earnings')

  if (!botId || isNaN(botId)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">無效的 Bot ID</div>
      </div>
    )
  }

  // Handle error dismissal
  const handleDismissError = () => {
    setDismissedError(true)
  }

  // Reset dismissed error when error changes
  const currentError = statusData?.last_error || null
  if (currentError && dismissedError && statusData) {
    // If error changed, reset dismissedError
    setDismissedError(false)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Bot 監控中心</h1>
          <p className="mt-2 text-sm text-gray-600">Bot ID: {botId}</p>
        </div>

        {/* Error Banner */}
        {statusData?.last_error && !dismissedError && (
          <div className="mb-6">
            <ErrorBanner
              message={`Bot 錯誤：${statusData.last_error}`}
              onClose={handleDismissError}
              type="error"
            />
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('status')}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === 'status'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                即時狀態
              </button>
              <button
                onClick={() => setActiveTab('loans')}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === 'loans'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                貸款
              </button>
              <button
                onClick={() => setActiveTab('earnings')}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === 'earnings'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                收益分析
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'status' && (
          <div className="space-y-6">
            {/* Network Error Banner */}
            {statusError && (
              <ErrorBanner
                message="連線失敗，嘗試重新連線中..."
                onClose={() => {}}
                type="warning"
              />
            )}

            {/* Status Panel */}
            {statusLoading && !statusData && (
              <div className="text-center py-12">
                <div className="text-gray-600">載入中...</div>
              </div>
            )}
            {statusData && <StatusPanel status={statusData} />}

            {/* Offers Table */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">掛單列表</h2>
              <OffersTable
                offers={offersData?.offers || []}
                currency={statusData?.currency || 'USD'}
                isLoading={offersLoading}
                error={offersError}
              />
            </div>
          </div>
        )}

        {activeTab === 'loans' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">貸款列表</h2>
            <LoansTable
              loans={loansData?.loans || []}
              currency={statusData?.currency || 'USD'}
              isLoading={loansLoading}
              error={loansError}
            />
          </div>
        )}

        {activeTab === 'earnings' && (
          <div className="bg-white rounded-lg shadow p-6">
            <EarningsChart
              data={Array.isArray(earningsData) ? earningsData : []}
              currency={statusData?.currency || 'USD'}
              isLoading={earningsLoading}
              error={earningsError}
              onDaysChange={setEarningsDays}
              currentDays={earningsDays}
            />
          </div>
        )}
      </div>
    </div>
  )
}
